import { Box, Button, Center, Stack, Text } from '@chakra-ui/react'
import { useSlate } from 'slate-react'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import uploadFile from '@/client/services/userUpload/uploadFile'

import FileUploadDropzone from '@/components/FileUploadDropzone/FileUploadDropzone'
import Spinner from '@/components/Spinner'
import ModalV3 from '@/components/UI/Modal/ModalV3'

import { insertImage } from './image'

export default function UploadImageModal({
  showModal,
  setShowModal,
  imageUpload,
  setImageUpload,
}: {
  showModal: boolean
  setShowModal: (show: boolean) => void
  imageUpload: File | null
  setImageUpload: React.Dispatch<React.SetStateAction<File | null>>
}): React.ReactElement {
  const editor = useSlate()

  const [onSaveImageUpload, isLoading] = useAPIWrapper(
    async () => {
      if (imageUpload == null) {
        return
      }

      try {
        const { downloadUrl } = await uploadFile({
          file: imageUpload,
          fileType: imageUpload.type,
          target: {
            type: 'attachment',
          },
        })
        insertImage(editor, downloadUrl)
      } finally {
        setShowModal(false)
      }
    },
    [imageUpload, editor, setShowModal],
    'Upload Image'
  )

  return (
    <ModalV3
      size="lg"
      title="Upload Image"
      open={showModal}
      onClose={() => {
        setShowModal(false)
      }}
      rightFooterContent={() => (
        <Stack justify="flex-end" spacing={2} direction="row" mt={4}>
          <Button
            variant="danger-outline"
            onClick={() => {
              setShowModal(false)
            }}
          >
            Cancel
          </Button>
          <Button onClick={onSaveImageUpload}>Save</Button>
        </Stack>
      )}
      body={() =>
        isLoading ? (
          <Center>
            <Box textAlign="center">
              <Text mb={6}>Uploading image...</Text>
              <Spinner />
            </Box>
          </Center>
        ) : (
          <Box>
            <FileUploadDropzone
              maxFiles={1}
              maxSizeMegabytes={5}
              acceptedFileTypes={['image/jpeg', 'image/png', 'image/gif']}
              onChange={files => {
                setImageUpload(files[0] ?? null)
              }}
              initialFiles={imageUpload == null ? [] : [imageUpload]}
            />
          </Box>
        )
      }
    />
  )
}
